/* ===========================================================================
   RATEGROUP TEMP MOCK - remove in the follow-up cleanup MR (COA-4227)

   The BE does not return restrictions or restriction recommendations for CEMA
   rate groups yet. Without them every rate group row on the MinLOS hovercard
   resolves to `--`, so the feature cannot be reviewed, screenshotted, or shown
   to product.

   This fabricates rate group restriction data in the store so the hovercard
   renders its real states. The call site gates it behind the Deep Discount OR
   Deep Discount autopilot feature toggle, so only hotels that can show rate
   groups somewhere get fake records; testing on a feature branch env requires
   one of those toggles on the hotel.

   TO REMOVE: delete this file, then everything else tagged
   RATEGROUP TEMP MOCK (an import and a call site in
   `services/pricing-assistant-data-request-service.ts`).

   Scenarios cycle by day of month, so hovering across a month walks through
   them. See SCENARIOS below for what each day shows.
   =========================================================================== */

import type { Registry as Services } from '@ember/service';
import type { Period } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import {
  RESTRICTION_MODEL_CONTEXTS,
  type RestrictionModelContextsType,
} from 'lumen/utils/constants/channel-pricing-constants';
import {
  OVERRIDE_TYPES,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { ABSTRACT_ACTIONS } from 'lumen/utils/constants/restriction-actions';

const MOCK_ID_PREFIX = 'mock-rate-group';

interface MockRateGroupState {
  /* Renders the group in the failed restriction row. */
  failedValue?: number;
  /* Renders the value struck through under "hotel restriction applies". */
  hotelRestrictionApplies?: boolean;
  /* Renders the row as a loading skeleton. */
  isPushing?: boolean;
  /* Wins over `value` and `recommendation`, exercising the override precedence. */
  overrideValue?: number;
  /* Wins over `value`, exercising the recommendation precedence. */
  recommendation?: number;
  /* MinLOS the BE would have pushed. `null` leaves the group with no record,
     which is what renders `--`. */
  value: number | null;
}

interface MockScenario {
  /* `groups` maps onto the configured rate groups in order; groups beyond the
     end of the array reuse the last entry. */
  groups: MockRateGroupState[];
  /* Hotel-level MinLOS, so a scenario turning on hotel precedence reads
     coherently against the Hotel row. Only pushed when the period has no real
     hotel-level restriction, so it can never mask live data. */
  hotelValue?: number;
}

const SCENARIOS: MockScenario[] = [
  // 1st: a plain distinct value per group.
  { groups: [{ value: 2 }, { value: 3 }, { value: 4 }] },
  // 2nd: first two share a value and merge onto one row.
  { groups: [{ value: 2 }, { value: 2 }, { value: 5 }] },
  // 3rd: every group shares a value, so the label overflows to "N more...".
  { groups: [{ value: 3 }] },
  /* 4th: the hotel MinLOS is stricter than the first group's, so the BE
     attributes that group to the hotel: its own 2 is struck through and the
     Hotel row's 4 is what gets pushed. The second group asks for 6, which is
     stricter than the hotel, so it keeps its own value. */
  {
    groups: [{ hotelRestrictionApplies: true, value: 2 }, { value: 6 }],
    hotelValue: 4,
  },
  // 5th: a push in flight on the first group.
  { groups: [{ isPushing: true, value: 2 }, { value: 4 }, { value: 6 }] },
  // 6th: a failed push on the first group.
  { groups: [{ failedValue: 7, value: 2 }, { value: 4 }, { value: 6 }] },
  // 7th: an override and a recommendation competing on the first group.
  { groups: [{ overrideValue: 2, recommendation: 5, value: 9 }, { value: 4 }] },
  /* 8th: two groups reading the same value, only one overridden. They must
     render as two separate rows: `hasOverride` is part of the merge key
     because the indicator asserts an override for every group in the row. */
  { groups: [{ overrideValue: 3, value: 3 }, { value: 3 }] },
  // 9th: "No MinLOS restriction" next to a group with no record at all.
  { groups: [{ value: 0 }, { value: null }, { value: 3 }] },
  /* 10th: two groups both reading "MinLOS 2" with only one struck through, which
     is the one case the merge key's `hotelRestrictionApplies` clause exists for.
     It needs a recommendation to happen at all: precedence is decided against
     the group's own value (9 here), while the row displays the recommendation
     (2). Two plain restrictions sharing a value could not differ this way,
     because one hotel MinLOS judged against two equal values gives one answer. */
  {
    groups: [
      { hotelRestrictionApplies: true, recommendation: 2, value: 9 },
      { value: 2 },
    ],
    hotelValue: 5,
  },
  /* 11th: a rate group override weaker than the hotel MinLOS. The override is
     still pushed but the hotel applies, so the row shows the struck-through
     override and the override indicator together. */
  {
    groups: [
      { hotelRestrictionApplies: true, overrideValue: 2, value: 3 },
      { value: 6 },
    ],
    hotelValue: 4,
  },
];

interface MockOptions {
  periods: Period[];
  rateGroups: RateGroup[];
  store: Services['store'];
}

interface MinLosValueOptions {
  appliedContext?: RestrictionModelContextsType;
  value: number;
}

function minLosValues({ appliedContext, value }: MinLosValueOptions): {
  appliedContext?: RestrictionModelContextsType;
  type: string;
  value: number;
}[] {
  return [{ appliedContext, type: RESTRICTION_TYPES.MIN_LOS, value }];
}

function stateForGroup(
  groups: MockRateGroupState[],
  index: number,
): MockRateGroupState {
  return groups[index] ?? groups[groups.length - 1] ?? { value: null };
}

/* RATEGROUP TEMP MOCK: store.push bypasses the `hoteldate` transform, so
   `fromDate` must already be the deserialized value the transform would have
   produced (a Dayjs), not a date string. The push payload's typed attributes
   still declare the raw wire shape, so this cast bridges the two for this
   mock only. */
function mockFromDate(period: Period): string {
  return period.fromDate as unknown as string;
}

/**
 * Injects fake rate group MinLOS data onto already-loaded periods.
 *
 * Records are pushed with synthetic ids rather than created, so they are not
 * `isNew` and cannot be picked up by a save from the day override modal.
 */
export function mockRateGroupRestrictions({
  periods,
  rateGroups,
  store,
}: MockOptions): void {
  if (rateGroups.length === 0) {
    console.warn(
      '[RATEGROUP TEMP MOCK] No configured rate groups, nothing to fake.',
    );
    return;
  }

  periods.forEach((period) => {
    const periodId = period.id;

    if (periodId == null) {
      return;
    }

    const dayOfMonth = Number(period.fromDate.format('D'));
    const scenario = SCENARIOS[(dayOfMonth - 1) % SCENARIOS.length] ?? {
      groups: [],
    };
    const periodRelationship = {
      period: { data: { id: periodId, type: 'period' } },
    };

    /* Only when the period has none of its own, so real hotel data always wins
       and `hotelLevelRestriction`'s find() stays deterministic. */
    if (scenario.hotelValue != null && period.hotelLevelRestriction == null) {
      store.push({
        data: {
          attributes: {
            context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
            fromDate: mockFromDate(period),
            rateGroupId: null,
            values: minLosValues({ value: scenario.hotelValue }),
          },
          id: `${MOCK_ID_PREFIX}-${periodId}-hotel`,
          relationships: periodRelationship,
          type: 'restriction',
        },
      });
    }

    rateGroups.forEach((rateGroup, index) => {
      const state = stateForGroup(scenario.groups, index);
      const { rateGroupId } = rateGroup;
      const recordId = `${MOCK_ID_PREFIX}-${periodId}-${index}`;
      /* Precedence is read off whichever record supplies the displayed value,
         so put it on both rather than guessing which one wins. */
      const appliedContext =
        state.hotelRestrictionApplies === true
          ? RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL
          : RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL;

      if (state.value != null) {
        store.push({
          data: {
            attributes: {
              context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
              fromDate: mockFromDate(period),
              rateGroupId,
              values: minLosValues({ appliedContext, value: state.value }),
            },
            id: recordId,
            relationships: periodRelationship,
            type: 'restriction',
          },
        });
      }

      if (state.overrideValue != null) {
        store.push({
          data: {
            attributes: {
              fromDate: mockFromDate(period),
              rateGroupId,
              type: OVERRIDE_TYPES.MIN_LOS,
              value: state.overrideValue,
            },
            id: `${recordId}-override`,
            relationships: {
              restriction: { data: { id: recordId, type: 'restriction' } },
            },
            type: 'override',
          },
        });
      }

      if (state.recommendation != null) {
        store.push({
          data: {
            attributes: {
              context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
              fromDate: mockFromDate(period),
              rateGroupId,
              values: minLosValues({
                appliedContext,
                value: state.recommendation,
              }),
            },
            id: `${recordId}-recommendation`,
            relationships: periodRelationship,
            type: 'restriction-recommendation',
          },
        });
      }

      if (state.isPushing === true || state.failedValue != null) {
        store.push({
          data: {
            attributes: {
              action:
                state.failedValue != null
                  ? ABSTRACT_ACTIONS.EXCEPTION
                  : ABSTRACT_ACTIONS.PUSHING,
              context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
              fromDate: mockFromDate(period),
              rateGroupId,
              values: minLosValues({
                value: state.failedValue ?? state.value ?? 0,
              }),
            },
            id: `${recordId}-action`,
            relationships: periodRelationship,
            type: 'restriction-action',
          },
        });
      }
    });
  });
}
